import React from 'react';
import { Camera, Images } from 'lucide-react';

// Type for the photo upload handler
type PhotoUploadHandler = () => void;

/**
 * Renders a modern, enhanced button for continuous camera capture.
 */
export const renderRoomCameraButton = (
    roomIndex: number, 
    itemIndex: number, 
    handlePhotoUpload: PhotoUploadHandler,
    isProcessing?: boolean
): JSX.Element => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   (window as any).Capacitor !== undefined;
                   
  return (
    <button
      onClick={() => {
        if (isProcessing) return;
        console.log(`[DEBUG] Camera button clicked for R${roomIndex}I${itemIndex}`);
        handlePhotoUpload();
      }}
      disabled={isProcessing}
      className={`group relative flex items-center gap-2 px-4 py-2.5 font-medium rounded-lg shadow-md transform transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        isProcessing 
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
          : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white hover:shadow-lg hover:scale-105 focus:ring-emerald-500'
      }`}
      aria-label="Take multiple photos with camera"
    >
      <div className="relative">
        {isProcessing ? (
          <svg className="animate-spin h-[18px] w-[18px]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <div className="relative">
            <Camera size={18} className="group-hover:animate-pulse transition-transform duration-200" />
            <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center">
              <div className="text-[8px] font-bold bg-yellow-400 text-yellow-800 rounded-full w-3.5 h-3.5 flex items-center justify-center">+</div>
            </div>
          </div>
        )}
        {!isProcessing && (
          <div className="absolute inset-0 bg-white rounded-full opacity-0 group-active:opacity-20 group-active:animate-ping transition-opacity duration-150"></div>
        )}
      </div>
      <span className="text-sm font-medium flex items-center">
        {isProcessing ? 'Processing...' : 'Camera'}
        {!isProcessing && (
          <span className="ml-1 text-[10px] bg-yellow-400 text-yellow-800 rounded-full px-1.5 py-0.5 leading-none">FAST</span>
        )}
      </span>
      {!isProcessing && (
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 rounded-lg transition-opacity duration-200"></div>
      )}
    </button>
  );
};

/**
 * Renders a modern, enhanced button for selecting photos from device gallery.
 */
export const renderRoomUploadButton = (
    handleUpload: PhotoUploadHandler,
    isProcessing?: boolean
): JSX.Element => {
  return (
    <button
      className={`group relative flex items-center gap-2 px-4 py-2.5 font-medium rounded-lg shadow-md transform transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        isProcessing 
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
          : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white hover:shadow-lg hover:scale-105 focus:ring-blue-500'
      }`}
      onClick={() => {
        if (isProcessing) return;
        console.log(`[DEBUG] Gallery button clicked`);
        handleUpload();
      }}
      disabled={isProcessing}
      aria-label="Select photos from gallery"
    >
      <div className="relative">
        {isProcessing ? (
          <svg className="animate-spin h-[18px] w-[18px]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <Images size={18} className="group-hover:animate-pulse transition-transform duration-200" />
        )}
      </div>
      <span className="text-sm font-medium flex items-center gap-1">
        {isProcessing ? 'Processing...' : 'Gallery'}
      </span>
      {!isProcessing && (
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 rounded-lg transition-opacity duration-200"></div>
      )}
    </button>
  );
}; 